import {
  type PropsWithChildren,
  useContext,
  useMemo,
  createContext,
  useEffect,
  useCallback,
  useState,
} from "react";

import {
  type LanguageCode,
  type CountryCode,
  type Maybe,
} from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import getProductSelectorByIdQuery from "@graphql/get-product-selector-by-id.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  type ProductComboOption,
  type ProductSelectedVariantsByIndex,
  type ThemeMode,
} from "@root/tablescape/state";
import { STOREFRONT_API_URL } from "@tablescape/constants";

export type ProductSelectorSettings = {
  meta: {
    localization: { country: CountryCode; language: LanguageCode };
  };
  metaobjectId: string;
  productId: string;
  settings: {
    intelligemsEnabled: boolean;
    klarnaEnabled: boolean;
    theme: ThemeMode;
  };
  text: {
    selectOption: string;
  };
};

type ProductSelectorConnection = {
  data?: {
    metaobject: Maybe<{
      label: Maybe<{ value: string }>;
      productComboOptions: {
        references: { nodes: { id: string }[] };
      };
      selectedIndex: Maybe<{ value: number }>;
    }>;
  };
};

export const useProductSelectorParams = [
  STOREFRONT_API_URL,
  getProductSelectorByIdQuery,
];

type ProductSelectorDetails = {
  defaultSelectedIndex: number;
  isLoading: boolean;
  label: string | undefined;
  productComboOptionIds: string[];

  selectedIndex: number;
  selectedProductComboOption: ProductComboOption;
  selectedVariantsByIndex: ProductSelectedVariantsByIndex;
  setSelectedIndex: (selectedIndex: number) => void;
  setSelectedProductComboOption: (
    selectedProductComboOption: ProductComboOption
  ) => void;
  setSelectedVariantsByIndex: (
    variantByIndex: ProductSelectedVariantsByIndex
  ) => void;
};

const ProductSelectorContext = createContext<
  ProductSelectorDetails | undefined
>(undefined);

type Props = {
  id?: string;
  value?: ProductSelectorDetails;
};

function ProductSelectorProvider({
  children,
  id, // Global ID of product selector that can be manually passed into the provider (for React Product hero)
  value: providedValue,
}: PropsWithChildren<Props>) {
  const {
    meta: {
      localization: { country, language },
    },
    metaobjectId, // Global ID of product selector that is pulled from section settings (for Liquid Product Hero)
  } = useSectionSettings<ProductSelectorSettings>();

  const { data, isLoading } = useSWR<ProductSelectorConnection>(
    (id || metaobjectId) && country && language
      ? [
          ...useProductSelectorParams,
          {
            metaobjectId: id ?? metaobjectId,
            country,
            language,
          },
        ]
      : null,
    storefrontAPIFetcher,
    {}
  );

  const productSelector = data?.data?.metaobject;

  const productComboOptionIds = useMemo(
    () =>
      productSelector?.productComboOptions?.references.nodes.map(
        (node) => node.id
      ) ?? [],
    [productSelector?.productComboOptions?.references?.nodes]
  );

  const defaultSelectedIndex = useMemo(() => {
    if (isLoading) {
      return -1;
    }

    if (productSelector?.selectedIndex?.value) {
      return Number(productSelector.selectedIndex.value) - 1;
    }

    return 0;
  }, [isLoading, productSelector?.selectedIndex?.value]);

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [selectedProductComboOption, setSelectedProductComboOption] =
    useState<ProductComboOption>({});
  const [selectedVariantsByIndex, setSelectedVariantsByIndex] =
    useState<ProductSelectedVariantsByIndex>({});

  const setProductSelectorIndex = useCallback(
    (selectedIndex: number) => {
      setSelectedIndex(selectedIndex);
    },
    [setSelectedIndex]
  );

  const setSelectedVariantsByProductSelectorIndex = useCallback(
    (variantByIndex: ProductSelectedVariantsByIndex) => {
      setSelectedVariantsByIndex({
        ...selectedVariantsByIndex,
        ...variantByIndex,
      });
    },
    [selectedVariantsByIndex, setSelectedVariantsByIndex]
  );

  const setSelectedProductSelectorComboOption = useCallback(
    (selectedProductComboOption: ProductComboOption) => {
      setSelectedProductComboOption(selectedProductComboOption);
    },
    [setSelectedProductComboOption]
  );

  useEffect(() => {
    if (selectedIndex === -1 && defaultSelectedIndex !== -1) {
      setSelectedIndex(defaultSelectedIndex);
    }
  }, [defaultSelectedIndex, selectedIndex, setSelectedIndex]);

  const value = useMemo<ProductSelectorDetails>(
    () => ({
      ...providedValue,
      label: productSelector?.label?.value,
      productComboOptionIds,
      defaultSelectedIndex,
      isLoading,

      selectedIndex,
      selectedProductComboOption,
      selectedVariantsByIndex,
      setSelectedIndex: setProductSelectorIndex,
      setSelectedProductComboOption: setSelectedProductSelectorComboOption,
      setSelectedVariantsByIndex: setSelectedVariantsByProductSelectorIndex,
    }),
    [
      defaultSelectedIndex,
      isLoading,
      productComboOptionIds,
      productSelector?.label?.value,
      providedValue,
      selectedIndex,
      selectedProductComboOption,
      selectedVariantsByIndex,
      setProductSelectorIndex,
      setSelectedProductSelectorComboOption,
      setSelectedVariantsByProductSelectorIndex,
    ]
  );

  return (
    <ProductSelectorContext.Provider value={value}>
      {children}
    </ProductSelectorContext.Provider>
  );
}

function useProductSelector() {
  const context = useContext<ProductSelectorDetails | undefined>(
    ProductSelectorContext
  );

  if (context === undefined) {
    throw new Error(
      "useProductSelector must be used within a ProductSelectorProvider"
    );
  }

  return context;
}

export { ProductSelectorProvider, useProductSelector };
